import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Breadcrumb } from '../components/Breadcrumb';
import { PageTitle } from '../components/PageTitle';

const PAGETITLE_JP = '事業紹介';
const PAGETITLE_EN = 'Service';

interface ServiceCardProps {
  title: string;
  description: string;
  imageSrc: string;
  imageSrcSP: string;
  imageAlt: string;
  linkTo: string;
}

const ServiceSectionContainer = styled.div.attrs({className: 'page-container'})`
  background: #F9FAFA;
  min-height: 100vh;
  padding: 0 0 160px;

  @media (max-width: 768px) {
    padding: 0 20px 0;
`;







const ServiceContentContainer = styled.div.attrs({className: 'service-content-container'})`
  margin: 0 auto;
  padding: 80px 0 0;
  background-color: #F9FAFA;
  width: 95%;

  & > *:last-child {
      margin-bottom: 0;
    }

  @media (max-width: 768px) {
    padding: 60px 0 132px;
  }
  @media (min-width: 769px) {
    max-width: var(--contents-maxwidth-pc);
  }
`;

const ServiceCard = styled(Link).attrs({className: 'service-card'})`
  text-decoration: none;
  background: #FFFFFF;
  border-radius: 20px;
  padding: 60px 50px;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  gap: 70px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 27px;
    gap: 30px;
    margin-bottom: 30px;
    align-items: center;
  }
`;

const ServiceImage = styled.div<{ $imageSrc: string }>`
  width: 520px;
  height: 320px;
  border-radius: 20px;
  background-image: url(${props => props.$imageSrc});
  background-size: cover;
  background-position: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
    width: 100%;
    aspect-ratio: 4/3;
    border-radius: 20px;
    flex-shrink: 0;
    background-size: cover;
    background-position: center;
  }
`;

const ServiceImageSP = styled(ServiceImage)<{ $imageSrc: string }>`
  display: none;
  @media (max-width: 768px) {
    display: unset;
    width: 290px;
    height: 170px;
    }
`;

const ServiceContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 40px;

  @media (max-width: 768px) {
    gap: 20px;
    width: 100%;
    align-items: flex-start;
    text-align: left;
  }
`;

const ServiceTitle = styled.h3`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 1.75;
    letter-spacing: 0.05em;
    width: 100%;
  }
`;

const ServiceDescription = styled.p`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 1.75;
    width: 100%;
  }
`;

const ServiceButton = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.38;
  letter-spacing: 0.05em;
  color: #333333;
  padding: 0;
  transition: color 0.3s ease;
  text-decoration: none;

  &:hover {
    color: #0F3F8E;
  }

  @media (max-width: 768px) {
    gap: 20px;
    font-size: 13px;
    line-height: 1.38;
    letter-spacing: 0.05em;
    width: fit-content;
  }
`;

const DetailArrow = styled.div`
  width: 26px;
  height: 26px;
  background: #FFFFFF;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
  }
`;

const DetailArrowIcon = styled.img`
  width: 16px;
  height: 16px;

  @media (max-width: 768px) {
    width: 14px;
    height: 14px;
  }
`;

const ServiceCardComponent: React.FC<ServiceCardProps> = ({ title, description, imageSrc, imageSrcSP, imageAlt, linkTo }) => {
    useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ServiceCard to={linkTo}>
      <ServiceImage $imageSrc={imageSrc} />
      <ServiceImageSP $imageSrc={imageSrcSP} />
      <ServiceContent>
        <ServiceTitle>{title}</ServiceTitle>
        <div>
          <ServiceDescription>{description}</ServiceDescription>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '60px' }}>
            <ServiceButton>
              詳しく見る
              <DetailArrow>
                <DetailArrowIcon src="/images/arrow-right.svg" alt="arrow right" />
              </DetailArrow>
            </ServiceButton>
          </div>
        </div>
      </ServiceContent>
    </ServiceCard>
  );
};

const ServiceSection: React.FC = () => {
  const services = [
    {
      title: '開発',
      description: '確かな技術力と豊富な実績を持つ開発部門を有しており、大手通信会社の基幹システムをはじめその他大手企業の多岐にわたる開発案件を成功に導いてきました。クライアントのビジネスを深く理解し、最適なソリューションを提供することで、課題解決と事業成長に貢献します。',
      imageSrc: '/assets/images/service/develop_pc.jpg',
      imageSrcSP: '/assets/images/service/develop_sp.jpg',
      imageAlt: '開発サービス',
      linkTo: '/service/development/'
    },
    {
      title: 'クリエイティブ',
      description: 'クリエイティブ部門は、Webサイトやランディングページ制作からブランディング戦略立案まで、お客様のデジタルプレゼンスを最大化します。データに基づいた詳細なマーケティング分析と心を掴むコピーライティング、プロフェッショナルな撮影・動画編集を通じて、多角的にビジネスの成長を支援します。',
      imageSrc: '/assets/images/service/creative_pc.jpg',
      imageSrcSP: '/assets/images/service/creative_sp.jpg',
      imageAlt: 'クリエイティブサービス',
      linkTo: '/service/creative/'
    },
    {
      title: 'プロダクト',
      description: '作り手の気持ちを理解したいという想いから、デジタル領域の知見とものづくりへの情熱を融合し、バッグブランド「FADEN®︎」を展開しています。クリエイティブ部門のクリエイティブディレクターが全工程を担当し、大手セレクトショップをはじめ全国で流通する多機能バッグブランドです。',
      imageSrc: '/assets/images/service/faden_520_320.jpg',
      imageSrcSP: '/assets/images/service/faden_290_170.jpg',
      imageAlt: 'プロダクトサービス',
      linkTo: '/service/product/'
    }
  ];

  return (
    <ServiceSectionContainer>
      {/* Breadcrumb Navigation */}
      {/* <Breadcrumb currentPageTitle={ PAGETITLE_JP } /> */}
      <Breadcrumb 
        items={[
          { label: PAGETITLE_JP } // 現在のページ
        ]} 
      />


      {/* Page Title Section */}
      <PageTitle title={ PAGETITLE_JP } subtitle={ PAGETITLE_EN } />


      {/* Service Content */}
      <ServiceContentContainer>
        {services.map((service, index) => (
          <ServiceCardComponent
            key={index}
            title={service.title}
            description={service.description}
            imageSrc={service.imageSrc}
            imageSrcSP={service.imageSrcSP}
            imageAlt={service.imageAlt}
            linkTo={service.linkTo}
          />
        ))}
      </ServiceContentContainer>
    </ServiceSectionContainer>
  );
};

export default ServiceSection;
